import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { handleOAuthCallback } from '../services/auth'
import { useAuth } from '../contexts/AuthContext'

export function AuthCallback() {
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { refreshAuth } = useAuth()
  const callbackRef = useRef<Promise<void> | null>(null)

  useEffect(() => {
    if (callbackRef.current) {
      return;
    }

    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      const errorParam = params.get('error')

      if (errorParam) {
        setError(`Discord authorization failed: ${errorParam}`)
        return
      }

      if (!code) {
        setError('No authorization code received from Discord')
        return
      }

      try {
        await handleOAuthCallback(code)

        // Refresh auth context to pick up the new user
        await refreshAuth()

        // If we got here, the user has been authenticated
        if (import.meta.env.MODE === 'development') {
          navigate('/?code=' + encodeURIComponent(code))
        } else {
          navigate('/')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to complete authentication')
      }
    }

    callbackRef.current = handleCallback()
  }, [refreshAuth, navigate])

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-discord-dark to-gray-900">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-discord-red text-5xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-discord-blurple hover:bg-discord-blurple/90 text-white font-semibold rounded-lg transition-colors"
            >
              Return Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-discord-dark to-gray-900">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-discord-blurple mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Completing Login</h2>
          <p className="text-gray-600">Please wait while we finish setting up your session...</p>
        </div>
      </div>
    </div>
  )
}
